import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  UserPlus, Users as UsersIcon, Shield, ShieldCheck, Trash2, KeyRound,
  Loader2, X, Pencil, ArrowUpCircle, ArrowDownCircle, PowerOff, Power, Truck,
} from "lucide-react";
import CraneIcon from "@/components/CraneIcon";
import LiveSearch, { matchesQuery } from "@/components/LiveSearch";

const DEPARTAMENTOS = ["ADMIN", "CLIMA", "CONS", "EST", "ETEL", "FONT/PCI", "FV", "PDS"];
const EMPRESAS = ["SUNTELCO", "SUNFRICA"];

const emptyCreate = { name: "", email: "", password: "", role: "employee", empresa: "", departamento: "", can_clock_in: true };

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreate);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(null);
  const [busy, setBusy] = useState(null); // user_id under action
  const [editForm, setEditForm] = useState(null); // {id, name, role}
  const [query, setQuery] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const submitCreate = async (e) => {
    e.preventDefault();
    if (!createForm.name || !createForm.email || createForm.password.length < 6) {
      toast.error("Rellena nombre, email y contraseña (mín. 6 caracteres)");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/users", createForm);
      toast.success("Usuario creado");
      setCreateForm(emptyCreate);
      setCreateOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al crear");
    } finally {
      setSaving(false);
    }
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editForm) return;
    setSaving(true);
    try {
      const payload = {
        name: editForm.name.trim(),
        email: (editForm.email || "").trim().toLowerCase(),
        role: editForm.role,
        empresa: editForm.empresa || "",
        departamento: editForm.departamento || "",
      };
      // Only sadmins can toggle these sadmin-only flags
      if (me?.role === "sadmin") {
        payload.can_receive_vehicle_reports = !!editForm.can_receive_vehicle_reports;
        payload.can_view_crane_calendar = !!editForm.can_view_crane_calendar;
        payload.can_manage_crane_calendar = !!editForm.can_manage_crane_calendar;
        payload.can_clock_in = !!editForm.can_clock_in;
      }
      await api.put(`/admin/users/${editForm.id}`, payload);
      toast.success("Usuario actualizado");
      setEditForm(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  const toggleRole = async (u) => {
    const newRole = u.role === "admin" ? "employee" : "admin";
    const verb = newRole === "admin" ? "convertir en ADMINISTRADOR" : "quitar permisos de admin";
    if (!window.confirm(`¿Seguro que quieres ${verb} a ${u.name}?`)) return;
    setBusy(u.id);
    try {
      await api.put(`/admin/users/${u.id}`, { role: newRole });
      toast.success(newRole === "admin" ? "Ahora es administrador" : "Ahora es operario");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setBusy(null);
    }
  };

  const toggleActive = async (u) => {
    const goingActive = !u.is_active;
    const verb = goingActive ? "reactivar" : "desactivar";
    if (!window.confirm(`¿${verb.charAt(0).toUpperCase() + verb.slice(1)} a ${u.name}?`)) return;
    setBusy(u.id);
    try {
      await api.put(`/admin/users/${u.id}`, { is_active: goingActive });
      toast.success(goingActive ? "Usuario reactivado" : "Usuario desactivado");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setBusy(null);
    }
  };

  const resetPassword = async (u) => {
    const pwd = window.prompt(`Nueva contraseña para ${u.name} (mín. 6):`);
    if (!pwd) return;
    setResetting(u.id);
    try {
      await api.post(`/admin/users/${u.id}/reset-password`, { password: pwd });
      toast.success("Contraseña actualizada");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setResetting(null);
    }
  };

  const filtered = users.filter((u) =>
    matchesQuery(query, u.name, u.email, u.empresa, u.departamento, u.role)
  );
  const active = filtered.filter((u) => u.is_active !== false);
  const inactive = filtered.filter((u) => u.is_active === false);

  return (
    <div className="space-y-6" data-testid="admin-users">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-up text-[#FF4500]">Solo admin</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight mt-1">
            Gestión de usuarios
          </h1>
          <p className="text-zinc-600 text-sm mt-1">
            Crea, edita, desactiva y resetea contraseñas. Los usuarios desactivados conservan su historial.
          </p>
        </div>
        <button
          data-testid="add-user-btn"
          onClick={() => setCreateOpen(true)}
          className="brutal-btn-primary inline-flex items-center gap-2"
        >
          <UserPlus size={16} /> Nuevo usuario
        </button>
      </header>

      <LiveSearch
        value={query}
        onChange={setQuery}
        placeholder="Buscar por nombre, email, empresa o departamento…"
        testId="users-search"
      />

      <UserSection
        title="Activos"
        users={active}
        me={me}
        loading={loading}
        busy={busy}
        resetting={resetting}
        onEdit={(u) => setEditForm({
          id: u.id, name: u.name, email: u.email, role: u.role,
          empresa: u.empresa || "", departamento: u.departamento || "",
          can_receive_vehicle_reports: !!u.can_receive_vehicle_reports,
          can_view_crane_calendar: !!u.can_view_crane_calendar,
          can_manage_crane_calendar: !!u.can_manage_crane_calendar,
          can_clock_in: !!u.can_clock_in,
        })}
        onToggleRole={toggleRole}
        onToggleActive={toggleActive}
        onResetPassword={resetPassword}
      />

      {inactive.length > 0 && (
        <UserSection
          title="Desactivados"
          dimmed
          users={inactive}
          me={me}
          loading={false}
          busy={busy}
          resetting={resetting}
          onEdit={(u) => setEditForm({
            id: u.id, name: u.name, email: u.email, role: u.role,
            empresa: u.empresa || "", departamento: u.departamento || "",
            can_receive_vehicle_reports: !!u.can_receive_vehicle_reports,
            can_view_crane_calendar: !!u.can_view_crane_calendar,
            can_manage_crane_calendar: !!u.can_manage_crane_calendar,
            can_clock_in: !!u.can_clock_in,
          })}
          onToggleRole={toggleRole}
          onToggleActive={toggleActive}
          onResetPassword={resetPassword}
        />
      )}

      {/* Create modal */}
      {createOpen && (
        <Modal title="Nuevo usuario" onClose={() => setCreateOpen(false)} testId="add-user-modal">
          <form onSubmit={submitCreate} className="space-y-3">
            <Field label="Nombre" testId="new-user-name" value={createForm.name}
              onChange={(v) => setCreateForm({ ...createForm, name: v })} placeholder="Francisco Martínez" required />
            <Field label="Email" type="email" testId="new-user-email" value={createForm.email}
              onChange={(v) => setCreateForm({ ...createForm, email: v.toLowerCase() })} placeholder="operario@suntelco.es" required />
            <Field label="Contraseña (mín. 6)" testId="new-user-password" value={createForm.password}
              onChange={(v) => setCreateForm({ ...createForm, password: v })} placeholder="Mínimo 6 caracteres" required />
            <div>
              <label className="label-up block mb-1.5">Rol</label>
              <select data-testid="new-user-role" className="brutal-input w-full"
                value={createForm.role} onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}>
                <option value="employee">Operario</option>
                <option value="admin">Administrador</option>
                {me?.role === "sadmin" && <option value="sadmin">Super Administrador</option>}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-up block mb-1.5">Empresa</label>
                <select data-testid="new-user-empresa" className="brutal-input w-full"
                  value={createForm.empresa} onChange={(e) => setCreateForm({ ...createForm, empresa: e.target.value })}>
                  <option value="">—</option>
                  {EMPRESAS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="label-up block mb-1.5">Departamento</label>
                <select data-testid="new-user-departamento" className="brutal-input w-full"
                  value={createForm.departamento} onChange={(e) => setCreateForm({ ...createForm, departamento: e.target.value })}>
                  <option value="">—</option>
                  {DEPARTAMENTOS.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
            </div>
            <ModalActions onCancel={() => setCreateOpen(false)} saving={saving} submitTestId="submit-new-user"
              submitLabel="Crear" submitIcon={<UserPlus size={16} />} />
          </form>
        </Modal>
      )}

      {/* Edit modal */}
      {editForm && (
        <Modal title="Editar usuario" onClose={() => setEditForm(null)} testId="edit-user-modal">
          <form onSubmit={submitEdit} className="space-y-3">
            <Field label="Nombre" testId="edit-user-name" value={editForm.name}
              onChange={(v) => setEditForm({ ...editForm, name: v })} required />
            <Field label="Email" type="email" testId="edit-user-email" value={editForm.email || ""}
              onChange={(v) => setEditForm({ ...editForm, email: v.toLowerCase().trim() })} required />
            <div>
              <label className="label-up block mb-1.5">Rol</label>
              <select data-testid="edit-user-role" className="brutal-input w-full"
                value={editForm.role} onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}>
                <option value="employee">Operario</option>
                <option value="admin">Administrador</option>
                {me?.role === "sadmin" && <option value="sadmin">Super Administrador</option>}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-up block mb-1.5">Empresa</label>
                <select data-testid="edit-user-empresa" className="brutal-input w-full"
                  value={editForm.empresa || ""} onChange={(e) => setEditForm({ ...editForm, empresa: e.target.value })}>
                  <option value="">—</option>
                  {EMPRESAS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="label-up block mb-1.5">Departamento</label>
                <select data-testid="edit-user-departamento" className="brutal-input w-full"
                  value={editForm.departamento || ""} onChange={(e) => setEditForm({ ...editForm, departamento: e.target.value })}>
                  <option value="">—</option>
                  {DEPARTAMENTOS.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
            </div>
            {me?.role === "sadmin" && (
              <>
                <label
                  className="flex items-start gap-2 select-none cursor-pointer p-3 rounded-lg border border-emerald-200 bg-emerald-50/40"
                  data-testid="edit-user-clock-wrapper"
                >
                  <input
                    type="checkbox"
                    data-testid="edit-user-clock"
                    className="w-5 h-5 brutal-border accent-emerald-600 mt-0.5"
                    checked={!!editForm.can_clock_in}
                    onChange={(e) => setEditForm({ ...editForm, can_clock_in: e.target.checked })}
                  />
                  <div>
                    <div className="text-sm font-bold uppercase tracking-wider text-emerald-800">
                      Puede fichar entrada / salida
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      Le habilita el botón «Fichar» en la app y recibe recordatorios de entrada/salida.
                    </div>
                  </div>
                </label>

                <label
                  className="flex items-start gap-2 select-none cursor-pointer p-3 rounded-lg border border-slate-200 bg-slate-50/50"
                  data-testid="edit-user-vehicle-reports-wrapper"
                >
                  <input
                    type="checkbox"
                    data-testid="edit-user-vehicle-reports"
                    className="w-5 h-5 brutal-border accent-[#0E78C2] mt-0.5"
                    checked={!!editForm.can_receive_vehicle_reports}
                    onChange={(e) => setEditForm({ ...editForm, can_receive_vehicle_reports: e.target.checked })}
                  />
                  <div>
                    <div className="text-sm font-bold uppercase tracking-wider text-[#0B2A4A]">
                      Recibir partes de vehículos
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      Notificación push cuando haya un NO en la revisión semanal.
                    </div>
                  </div>
                </label>

                <label
                  className="flex items-start gap-2 select-none cursor-pointer p-3 rounded-lg border border-amber-200 bg-amber-50/40"
                  data-testid="edit-user-crane-view-wrapper"
                >
                  <input
                    type="checkbox"
                    data-testid="edit-user-crane-view"
                    className="w-5 h-5 brutal-border accent-[#F5A623] mt-0.5"
                    checked={!!editForm.can_view_crane_calendar || !!editForm.can_manage_crane_calendar}
                    disabled={!!editForm.can_manage_crane_calendar}
                    onChange={(e) => setEditForm({ ...editForm, can_view_crane_calendar: e.target.checked })}
                  />
                  <div>
                    <div className="text-sm font-bold uppercase tracking-wider text-[#8B4513] inline-flex items-center gap-1.5">
                      <CraneIcon size={14} /> Ver calendario de grúa
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      Puede consultar cuándo está reservada la grúa.
                    </div>
                  </div>
                </label>

                <label
                  className="flex items-start gap-2 select-none cursor-pointer p-3 rounded-lg border border-amber-300 bg-amber-100/40 ml-4"
                  data-testid="edit-user-crane-manage-wrapper"
                >
                  <input
                    type="checkbox"
                    data-testid="edit-user-crane-manage"
                    className="w-5 h-5 brutal-border accent-[#F5A623] mt-0.5"
                    checked={!!editForm.can_manage_crane_calendar}
                    onChange={(e) => setEditForm({
                      ...editForm,
                      can_manage_crane_calendar: e.target.checked,
                      // grant view automatically when giving manage
                      can_view_crane_calendar: e.target.checked ? true : editForm.can_view_crane_calendar,
                    })}
                  />
                  <div>
                    <div className="text-sm font-bold uppercase tracking-wider text-[#8B4513]">
                      Modificar calendario de grúa
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      Puede reservar y cancelar reservas. Implica «Ver».
                    </div>
                  </div>
                </label>
              </>
            )}
            <ModalActions onCancel={() => setEditForm(null)} saving={saving} submitTestId="submit-edit-user"
              submitLabel="Guardar cambios" submitIcon={<Pencil size={16} />} />
          </form>
        </Modal>
      )}
    </div>
  );
}

/* ---------- helpers ---------- */
function formatLastLogin(iso) {
  if (!iso) return "Nunca ha accedido";
  const then = new Date(iso);
  if (isNaN(then.getTime())) return "Nunca ha accedido";
  const now = new Date();
  const diff = now - then;
  const min = Math.floor(diff / 60000);
  if (min < 1) return "Último acceso: ahora mismo";
  if (min < 60) return `Último acceso: hace ${min} min`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `Último acceso: hace ${hrs} h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `Último acceso: hace ${days} d`;
  if (days < 30) {
    const weeks = Math.floor(days / 7);
    return `Último acceso: hace ${weeks} sem`;
  }
  return `Último acceso: ${then.toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" })}`;
}

function UserSection({ title, users, me, loading, busy, resetting, dimmed,
  onEdit, onToggleRole, onToggleActive, onResetPassword }) {
  return (
    <section className={`brutal-card p-5 ${dimmed ? "opacity-90" : ""}`}>
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display font-extrabold text-xl inline-flex items-center gap-2">
          <UsersIcon size={20} /> {title} ({users.length})
        </h2>
        {loading && <Loader2 size={16} className="animate-spin text-slate-400" />}
      </div>
      {users.length === 0 ? (
        <p className="text-sm text-slate-500">Sin usuarios.</p>
      ) : (
        <ul className="divide-y divide-slate-200">
          {users.map((u) => (
            <li key={u.id} className={`py-3 flex items-center gap-3 flex-wrap ${dimmed ? "opacity-60" : ""}`} data-testid={`user-${u.id}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0 ${u.role === "admin" ? "bg-[#0B2A4A]" : "bg-[#F5A623]"}`}>
                {(u.name || u.email).slice(0, 1).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate flex items-center gap-2 flex-wrap">
                  {u.name}
                  {u.role === "admin" ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[#0B2A4A] text-white">
                      <ShieldCheck size={10} /> Admin
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-700 border border-slate-200">
                      <Shield size={10} /> Operario
                    </span>
                  )}
                  {u.id === me?.id && (
                    <span className="text-[10px] font-bold uppercase tracking-wider text-[#F5A623]">(tú)</span>
                  )}
                  {u.can_receive_vehicle_reports && (
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-sky-100 text-sky-800 border border-sky-300"
                      title="Recibe notificación cuando hay una incidencia en la revisión semanal de vehículo"
                      data-testid={`vehicle-badge-${u.id}`}
                    >
                      <Truck size={10} /> Partes vehículo
                    </span>
                  )}
                  {u.can_manage_crane_calendar && (
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-300"
                      title="Puede reservar y modificar el calendario de la grúa"
                      data-testid={`crane-manage-badge-${u.id}`}
                    >
                      <CraneIcon size={10} /> Grúa: gestor
                    </span>
                  )}
                  {u.can_view_crane_calendar && !u.can_manage_crane_calendar && (
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-200"
                      title="Puede consultar el calendario de la grúa"
                      data-testid={`crane-view-badge-${u.id}`}
                    >
                      <CraneIcon size={10} /> Grúa: ver
                    </span>
                  )}
                  {!u.is_active && (
                    <span className="text-[10px] font-bold uppercase tracking-wider text-red-600">desactivado</span>
                  )}
                </div>
                <div className="text-xs text-slate-500 truncate">{u.email}</div>
                <div className="text-[11px] text-slate-400 mt-0.5" data-testid={`last-login-${u.id}`}>
                  {formatLastLogin(u.last_login_at)}
                </div>
              </div>
              <div className="flex gap-1.5 ml-auto">
                <IconBtn onClick={() => onEdit(u)} testId={`edit-${u.id}`} title="Editar"><Pencil size={13} /></IconBtn>
                {u.id !== me?.id && (
                  <IconBtn
                    onClick={() => onToggleRole(u)} testId={`role-${u.id}`}
                    title={u.role === "admin" ? "Quitar admin" : "Hacer admin"}
                    busy={busy === u.id}
                  >
                    {u.role === "admin"
                      ? <ArrowDownCircle size={13} />
                      : <ArrowUpCircle size={13} />}
                  </IconBtn>
                )}
                <IconBtn onClick={() => onResetPassword(u)} testId={`reset-${u.id}`}
                  title="Resetear contraseña" busy={resetting === u.id}>
                  <KeyRound size={13} />
                </IconBtn>
                {u.id !== me?.id && (
                  u.is_active ? (
                    <IconBtn onClick={() => onToggleActive(u)} testId={`deactivate-${u.id}`}
                      title="Desactivar" hoverDanger busy={busy === u.id}>
                      <PowerOff size={13} />
                    </IconBtn>
                  ) : (
                    <IconBtn onClick={() => onToggleActive(u)} testId={`reactivate-${u.id}`}
                      title="Reactivar" busy={busy === u.id}>
                      <Power size={13} />
                    </IconBtn>
                  )
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function IconBtn({ children, onClick, testId, title, busy, hoverDanger }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      title={title}
      disabled={busy}
      className={`h-9 w-9 rounded-lg border border-slate-200 text-slate-600 inline-flex items-center justify-center transition-colors disabled:opacity-50 ${
        hoverDanger ? "hover:bg-red-500 hover:text-white hover:border-red-500" : "hover:bg-slate-100"
      }`}
    >
      {busy ? <Loader2 size={13} className="animate-spin" /> : children}
    </button>
  );
}

function Modal({ title, onClose, children, testId }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl border border-slate-200 shadow-2xl w-full max-w-md p-6"
        onClick={(e) => e.stopPropagation()}
        data-testid={testId}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-extrabold text-xl">{title}</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center">
            <X size={16} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, type = "text", value, onChange, placeholder, testId, required }) {
  return (
    <div>
      <label className="label-up block mb-1.5">{label}</label>
      <input
        data-testid={testId}
        type={type}
        className="brutal-input w-full"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
      />
    </div>
  );
}

function ModalActions({ onCancel, saving, submitTestId, submitLabel, submitIcon }) {
  return (
    <div className="pt-2 flex gap-2">
      <button type="button" onClick={onCancel} className="brutal-btn-ghost flex-1">
        Cancelar
      </button>
      <button
        type="submit"
        disabled={saving}
        data-testid={submitTestId}
        className="brutal-btn-primary flex-1 inline-flex items-center justify-center gap-2"
      >
        {saving ? <Loader2 className="animate-spin" size={16} /> : submitIcon}
        {submitLabel}
      </button>
    </div>
  );
}
